/**
 * Wishlist - shared client-side state for the heart/save feature.
 *
 * Storage: localStorage only (per-browser, no login required). Every
 * add/remove also fires a Klaviyo track event (when Klaviyo's onsite
 * script is present) so saved products can be used for retargeting
 * flows/segments once a visitor is identified.
 *
 * Any markup with [data-wishlist-toggle] (see snippets/wishlist-heart.liquid)
 * is wired up automatically via event delegation, so it works for cards
 * that get added to the page later (quick shop, infinite scroll, etc.).
 *
 * Public API: window.Wishlist = { add, remove, toggle, has, getAll, count, subscribe }
 */
(function () {
  var STORAGE_KEY = 'maviene:wishlist';
  var config = window.wishlistConfig || {};
  var listeners = [];
  var items = read();

  function read() {
    try {
      var raw = localStorage.getItem(STORAGE_KEY);
      return raw ? JSON.parse(raw) : [];
    } catch (e) {
      return [];
    }
  }

  function write() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    } catch (e) {}
  }

  function has(id) {
    id = String(id);
    return items.some(function (item) { return String(item.id) === id; });
  }

  function getAll() {
    return items.slice();
  }

  function count() {
    return items.length;
  }

  function trackKlaviyo(event, product) {
    if (!window.klaviyo || typeof window.klaviyo.push !== 'function') return;
    window.klaviyo.push(['track', event, {
      ProductID: product.id,
      ProductName: product.title,
      ProductURL: product.url,
      ProductImageURL: product.image,
      Price: product.price ? Number(product.price) / 100 : undefined
    }]);
  }

  function notify(justAddedId) {
    var snapshot = getAll();
    listeners.forEach(function (fn) {
      try { fn(snapshot); } catch (e) {}
    });
    document.dispatchEvent(new CustomEvent('wishlist:change', { detail: { items: snapshot } }));
    syncButtons(justAddedId);
    syncCount();
  }

  function add(product) {
    if (!product || product.id == null || product.id === '' || has(product.id)) return;
    items.push({
      id: product.id,
      title: product.title || '',
      url: product.url || '',
      image: product.image || '',
      price: product.price ? parseInt(product.price, 10) : null,
      variantId: product.variantId || null,
      available: product.available !== 'false' && product.available !== false,
      addedAt: Date.now()
    });
    write();
    trackKlaviyo('Added to Wishlist', product);
    notify(product.id);
  }

  function remove(id) {
    var removed = items.filter(function (item) { return String(item.id) === String(id); })[0];
    items = items.filter(function (item) { return String(item.id) !== String(id); });
    write();
    if (removed) trackKlaviyo('Removed from Wishlist', removed);
    notify();
  }

  function toggle(product) {
    if (!product || product.id == null || product.id === '') return;
    if (has(product.id)) {
      remove(product.id);
    } else {
      add(product);
    }
  }

  function subscribe(fn) {
    listeners.push(fn);
    return function unsubscribe() {
      listeners = listeners.filter(function (l) { return l !== fn; });
    };
  }

  function syncCount() {
    document.querySelectorAll('[data-wishlist-count]').forEach(function (el) {
      el.textContent = count();
      count() > 0 ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
    });
  }

  function syncButtons(justAddedId) {
    document.querySelectorAll('[data-wishlist-toggle]').forEach(function (btn) {
      var id = btn.getAttribute('data-product-id');
      var active = has(id);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      var label = active ? config.removeLabel : config.addLabel;
      if (label) btn.setAttribute('aria-label', label);
      if (active && justAddedId != null && String(id) === String(justAddedId)) {
        btn.classList.remove('wishlist-heart--pop');
        void btn.offsetWidth; // restart the animation even if it just ran
        btn.classList.add('wishlist-heart--pop');
      }
    });
  }

  function productFromButton(btn) {
    return {
      id: btn.getAttribute('data-product-id'),
      title: btn.getAttribute('data-product-title'),
      url: btn.getAttribute('data-product-url'),
      image: btn.getAttribute('data-product-image'),
      price: btn.getAttribute('data-wishlist-price'),
      variantId: btn.getAttribute('data-variant-id'),
      available: btn.getAttribute('data-product-available')
    };
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-wishlist-toggle]');
    if (!btn) return;
    e.preventDefault();
    toggle(productFromButton(btn));
  });

  function init() {
    syncButtons();
    syncCount();
  }
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.Wishlist = {
    add: add,
    remove: remove,
    toggle: toggle,
    has: has,
    getAll: getAll,
    count: count,
    subscribe: subscribe
  };
})();
